import React, { useEffect, useMemo, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from "../../components/Common/AppOverlay";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import { showLoading } from "react-global-loading";
import { ErrorNotify } from "../../shared/Alert";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import "./Standards.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const statusInfo = (status) => {
  if (status === 1) return { label: "Active", tone: "active" };
  if (status === 2) return { label: "Archived", tone: "archived" };
  return { label: "Draft", tone: "draft" };
};

const CodeActionMenu = ({ item, codeLabel, onOpen, lifecycleActions }) => {
  return (
    <ActionMenu
      label={item.name}
      actions={[
        {
          label: "Edit details",
          icon: <i className="bx bx-pencil" />,
          onSelect: () => onOpen(item.id),
        },
        {
          label: `View ${codeLabel.toLowerCase()}`,
          icon: <i className="bx bx-show" />,
          onSelect: () => onOpen(item.id),
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const lifecycleTypes = {
  "checklist-areas": "checklist-area",
  "checklist-topics": "checklist-topic",
  "prima-service-codes": "service-code",
  "prima-must-cover-codes": "mandatory-service-code",
};

const CodeList = ({
  createRequest = 0,
  listCodes,
  DetailComponent,
  codeLabel,
  codesLabel,
  categoryLabel,
  searchPlaceholder,
  fileName,
}) => {
  const [codes, setCodes] = useState([]);
  const [selectedId, setSelectedId] = useState(0);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const loadData = () => {
    showLoading(true);
    listCodes()
      .then((response) => {
        setCodes(response?.data || []);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify(
          `Error occurred while fetching ${codesLabel.toLowerCase()}.`
        );
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const openDetail = (id = 0) => {
    setSelectedId(id);
    setIsDetailOpen(true);
  };

  const closeDetail = () => {
    setIsDetailOpen(false);
    setSelectedId(0);
    loadData();
  };

  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (createRequest) openDetail();
  }, [createRequest]);

  const counts = useMemo(
    () => ({
      total: codes.length,
      active: codes.filter((item) => item.status === 1).length,
      draft: codes.filter((item) => item.status === 0).length,
      archived: codes.filter((item) => item.status === 2).length,
    }),
    [codes]
  );

  const filteredCodes = useMemo(
    () =>
      codes.filter((item) => {
        const searchText = `${item.name || ""} ${
          item.description || ""
        }`.toLowerCase();
        return (
          (!search || searchText.includes(search.toLowerCase())) &&
          (status === "all" || String(item.status) === status)
        );
      }),
    [codes, search, status]
  );

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
  };

  const columns = [
    {
      id: "name",
      header: "Code",
      accessorKey: "name",
      minSize: 175,
      flex: 1,
      cell: ({ row, getValue }) => (
        <button
          type="button"
          className="standards-grid__name"
          style={{ fontSize: 13 }}
          onClick={() => openDetail(row.original.id)}
        >
          <span>{(getValue() || "C").charAt(0)}</span>
          {getValue()}
        </button>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      minSize: 270,
      flex: 1.7,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "—"}</span>
      ),
    },
    {
      id: "category",
      header: "Category",
      accessorFn: () => categoryLabel,
      minSize: 170,
      flex: 1,
      cell: ({ getValue }) => (
        <span className="standards-grid__tag">{getValue()}</span>
      ),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.source || "Internal",
      minSize: 115,
      flex: 0.75,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 115,
      flex: 0.75,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updatedOn",
      header: "Last Updated",
      accessorFn: (row) => formatDate(row.updatedOn),
      minSize: 145,
      flex: 0.9,
    },
    {
      id: "updatedBy",
      header: "Updated By",
      accessorFn: (row) => row.updatedByUser?.fullName || "Admin User",
      minSize: 130,
      flex: 0.8,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      minSize: 75,
      flex: 0,
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      cell: ({ row }) => (
        <CodeActionMenu
          item={row.original}
          codeLabel={codeLabel}
          onOpen={openDetail}
          lifecycleActions={
            lifecycleTypes[fileName]
              ? lifecycle.actions(lifecycleTypes[fileName], row.original)
              : []
          }
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content">
      <SummaryCards
        items={[
          [
            "bx-purchase-tag-alt",
            "blue",
            `Total ${codesLabel}`,
            counts.total,
            `All ${codesLabel.toLowerCase()}`,
          ],
          [
            "bx-check-circle",
            "green",
            `Active ${codesLabel}`,
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-time-five",
            "orange",
            `Draft ${codesLabel}`,
            counts.draft,
            `${
              counts.total ? Math.round((counts.draft / counts.total) * 100) : 0
            }% of total`,
          ],
          [
            "bx-archive-in",
            "red",
            `Archived ${codesLabel}`,
            counts.archived,
            `${
              counts.total
                ? Math.round((counts.archived / counts.total) * 100)
                : 0
            }% of total`,
          ],
        ]}
        ariaLabel="Page summary"
      />
      <section className="standards-grid-panel">
        
        <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel"
          aria-label={`${codesLabel} filters`}
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={searchPlaceholder}
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Draft</option>
                <option value="2">Archived</option>
              </select>
            </label>
            <label>
              <span>Category</span>
              <select defaultValue="all">
                <option value="all">All Categories</option>
              </select>
            </label>
            <label>
              <span>Source</span>
              <select defaultValue="all">
                <option value="all">All Sources</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button type="button" onClick={resetFilters}>Clear</button>
          </div>
        </section>}

          data={filteredCodes}
          columns={columns}
          onRefresh={loadData}
          fileName={fileName}
          recordLabel={codesLabel.toLowerCase()}
          showToolbar={false}
          initialPageSize={15}
          rowHeight={56}
          columnHeaderHeight={56}
          gridSx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#ffffff !important",
              borderBottom: "1px solid #e7ebf2",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
              { backgroundColor: "#ffffff !important", color: "#52627f" },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "#52627f",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: ".2px",
            },
            "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
            "& .MuiDataGrid-cell": {
              fontSize: 13,
              borderBottomColor: "#edf0f5",
              color: "#273957",
            },
            "& .MuiDataGrid-row": { minHeight: "56px !important" },
            "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
              { fontSize: 14, borderTopColor: "#e7ebf2" },
          }}
        />
      </section>
      <Offcanvas
        backdrop="static"
        className="standards-drawer"
        direction="end"
        isOpen={isDetailOpen}
        toggle={closeDetail}
      >
        <OffcanvasHeader toggle={closeDetail} className="canvasHeader">
          {selectedId ? `Edit ${codeLabel}` : `New ${codeLabel}`}
        </OffcanvasHeader>
        <OffcanvasBody>
          <DetailComponent detailId={selectedId} closeAction={closeDetail} />
        </OffcanvasBody>
      </Offcanvas>
      {lifecycle.dialog}
    </section>
  );
};

export default CodeList;
